/**
 * useFirestoreQuery - Generic hook for Firestore collection queries with React Query
 *
 * Features:
 * - Automatic caching via React Query
 * - Loading/error states
 * - Refetch on demand
 * - Type-safe results
 * - Automatic query key management
 *
 * Usage:
 * ```tsx
 * const { data, isLoading, error, refetch } = useFirestoreQuery({
 *   queryKey: queryKeys.school.familyFees.byFamily(schoolId, familyId),
 *   collectionPath: `schools/${schoolId}/familyFees`,
 *   constraints: [where('familyId', '==', familyId)],
 *   enabled: !!schoolId && !!familyId,
 * });
 * ```
 * ⚠️ Възлаганията и децата на семейството — САМО през `useAssignments` / `useChildren`: ключът е
 * общ за няколко екрана и носи `requireServer` (виж `NoServerResponseError`).
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  collection,
  query,
  getDocs,
  QueryConstraint,
  DocumentData,
  QuerySnapshot,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '../services/firebase';

/**
 * Отговор, дошъл САМО от локалния кеш: сървърът не е достигнат.
 *
 * С `persistentLocalCache` `getDocs` не хвърля, когато Firestore е недостъпен — връща кеша
 * с `metadata.fromCache` (SDK го прави само в състояние Offline: 10 s без отговор, провал на
 * първата връзка, повреден IndexedDB, или — при няколко раздела — когато основният раздел е
 * офлайн). Празен кеш тогава изглежда като „нищо няма“; кеш със стари данни — като пълен
 * списък без новите декларации („Чакащи 0“). И двете са лъжа. 19.10 родител (№ 1116) виждаше
 * празна „Декларации“ при 5 възлагания на сървъра — това е водещият кандидат, при родителя
 * НЕпотвърден. Затова при `requireServer` отговорът само от кеша е грешка, която екранът назовава.
 */
export class NoServerResponseError extends Error {
  constructor(collectionPath: string) {
    super(`No server response for ${collectionPath}: local cache only`);
    this.name = 'NoServerResponseError';
  }
}

/** По име, не само `instanceof` — грешката минава през React Query. */
export function isNoServerResponse(error: unknown): boolean {
  return error instanceof Error && error.name === 'NoServerResponseError';
}

/**
 * Options for useFirestoreQuery hook
 */
export interface UseFirestoreQueryOptions<T> {
  /** React Query cache key */
  queryKey: readonly unknown[];
  /** Firestore collection path (e.g., 'schools/abc123/assignments') */
  collectionPath: string;
  /** Firestore query constraints (where, orderBy, limit, etc.) */
  constraints?: QueryConstraint[];
  /** Whether the query should execute */
  enabled?: boolean;
  /** Transform function to map document data to desired type */
  transform?: (doc: DocumentData, docId: string) => T;
  /** Field name to use as ID (defaults to document ID) */
  idField?: string;
  /**
   * Резултат САМО от кеша → `NoServerResponseError` вместо данните (виж класа).
   * ⚠️ Еднакво за всички заявки с един и същ `queryKey`: React Query пази ЕДИН резултат на ключ.
   */
  requireServer?: boolean;
  /** Additional React Query options */
  queryOptions?: Omit<
    UseQueryOptions<T[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}

/**
 * Generic hook for querying Firestore collections
 *
 * @param options - Query configuration options
 * @returns React Query result with data, loading state, error, and refetch function
 */
export function useFirestoreQuery<T = DocumentData>({
  queryKey,
  collectionPath,
  constraints = [],
  enabled = true,
  transform,
  idField = 'id',
  requireServer = false,
  queryOptions,
}: UseFirestoreQueryOptions<T>) {
  return useQuery<T[], Error>({
    queryKey,
    queryFn: async (): Promise<T[]> => {
      const collectionRef = collection(db, collectionPath);
      const q = constraints.length > 0
        ? query(collectionRef, ...constraints)
        : query(collectionRef);

      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      if (requireServer && snapshot.metadata?.fromCache) {
        // Следа за случая „родителят вижда празно“ — без лични данни: колекцията (с училището) и
        // дали кешът е бил празен. Без нея и следващият случай би останал само хипотеза.
        Sentry.captureMessage('firestore-no-server-response', {
          level: 'warning',
          // По колекция: Dedupe на Sentry изпуска ЕДНАКВО съобщение подред (децата след възлаганията).
          fingerprint: ['firestore-no-server-response', collectionPath.split('/').pop() ?? ''],
          tags: { collection: collectionPath.split('/').pop() },
          extra: { collectionPath, cachedDocs: snapshot.size },
        });
        throw new NoServerResponseError(collectionPath);
      }

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        if (transform) {
          return transform(data, doc.id);
        }
        // Use custom idField if specified
        return { ...data, [idField]: doc.id } as T;
      });
    },
    enabled,
    ...queryOptions,
  });
}

/**
 * useFirestoreDocument - Hook for fetching a single Firestore document
 *
 * Usage:
 * ```tsx
 * const { data, isLoading, error } = useFirestoreDocument({
 *   queryKey: queryKeys.school.assignments.single(schoolId, assignmentId),
 *   documentPath: `schools/${schoolId}/assignments/${assignmentId}`,
 *   enabled: !!assignmentId,
 * });
 * ```
 */
import { doc, getDoc } from 'firebase/firestore';

export interface UseFirestoreDocumentOptions<T> {
  /** React Query cache key */
  queryKey: readonly unknown[];
  /** Full document path (e.g., 'schools/abc123/assignments/xyz789') */
  documentPath: string;
  /** Whether the query should execute */
  enabled?: boolean;
  /** Transform function to map document data to desired type */
  transform?: (doc: DocumentData, docId: string) => T;
  /** Additional React Query options */
  queryOptions?: Omit<
    UseQueryOptions<T | null, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}

/**
 * Hook for fetching a single Firestore document
 *
 * @param options - Query configuration options
 * @returns React Query result with data, loading state, error, and refetch function
 */
export function useFirestoreDocument<T = DocumentData>({
  queryKey,
  documentPath,
  enabled = true,
  transform,
  queryOptions,
}: UseFirestoreDocumentOptions<T>) {
  return useQuery<T | null, Error>({
    queryKey,
    queryFn: async (): Promise<T | null> => {
      const docRef = doc(db, documentPath);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data();
      if (transform) {
        return transform(data, snapshot.id);
      }
      return { ...data, id: snapshot.id } as T;
    },
    enabled,
    ...queryOptions,
  });
}

/**
 * Helper to create a school-scoped collection path
 */
export function schoolCollection(schoolId: string, collectionName: string): string {
  return `schools/${schoolId}/${collectionName}`;
}

/**
 * Helper to create a school-scoped document path
 */
export function schoolDocument(
  schoolId: string,
  collectionName: string,
  documentId: string
): string {
  return `schools/${schoolId}/${collectionName}/${documentId}`;
}
